'use client';

/* eslint-disable jsx-a11y/no-static-element-interactions */
import type { ProductInformation } from '@dealerportal/common';
import { type MouseEventHandler, useRef } from 'react';
import { MarkFavourite } from '../ProductCard/MarkFavourite';
import { MissingImage } from '../ProductCard/MissingImage';
import { useFavouriteToggle } from './useFavouriteToggle';

interface SwatchZoomProps {
  src: string;
  productInfo: ProductInformation;
  /**
   * How the image fills its box. `cover` (default) suits flooring textures,
   * which tile; `contain` keeps product renders (cabinets, fixtures) whole.
   */
  fit?: 'cover' | 'contain';
}

/**
 * Implements an image that you can zoom within on hover
 * For the product image on a PDP page
 */

export const SwatchZoom = (props: SwatchZoomProps) => {
  const zoomedDiv = useRef<HTMLDivElement>(null);
  const zoomedImage = useRef<HTMLImageElement>(null);
  const { isFavourite, toggleFavourite } = useFavouriteToggle(
    props.productInfo
  );
  const fit = props.fit ?? 'cover';

  const mouseOverZoom: MouseEventHandler<HTMLDivElement> = (e) => {
    if (zoomedImage.current == null || zoomedDiv.current == null) return;
    const ratio =
      zoomedImage.current.naturalHeight / zoomedImage.current.naturalWidth;
    const rect = zoomedDiv.current.getBoundingClientRect();
    const xPos = e.clientX - rect.left;
    const yPos = e.clientY - rect.top;
    const xPercent = `${xPos / (zoomedDiv.current.clientWidth / 100)}%`;
    const yPercent = `${yPos / ((zoomedDiv.current.clientWidth * ratio) / 100)}%`;
    Object.assign(zoomedDiv.current.style, {
      backgroundPosition: `${xPercent} ${yPercent}`,
      backgroundSize: `${zoomedImage.current.naturalWidth}px`,
    });
  };

  const mouseLeaveZoom: MouseEventHandler<HTMLDivElement> = () => {
    if (zoomedDiv.current == null) return;
    Object.assign(zoomedDiv.current.style, {
      backgroundPosition: 'center',
      backgroundSize: fit,
    });
  };

  const imageSrc = props.src?.includes('res.cloudinary.com')
    ? props.src.replace('https://images.floorforce.com/Textures/', '')
    : props.src;

  // CiC items (cabinets, door styles) often carry no brand; skip the "by".
  const { productName, brandName, colorName } = props.productInfo;
  const imageTitle = `${productName}${brandName ? ` by ${brandName}` : ''} - ${colorName}`;

  return (
    <div>
      {imageSrc && (
        <div
          ref={zoomedDiv}
          onMouseMove={mouseOverZoom}
          onMouseLeave={mouseLeaveZoom}
          style={{
            backgroundImage: `url(${imageSrc})`,
            height: '400px',
            width: '100%',
            backgroundSize: fit,
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundColor: fit === 'contain' ? '#fff' : undefined,
          }}
          title={imageTitle}
        />
      )}
      {!imageSrc && <MissingImage height={400} />}
      <MarkFavourite
        btnClass="!ui-absolute !ui-top-[2.5%] !ui-right-[2.5%] ui-size-[2.5rem]"
        iconClass="ui-size-[1.35rem]"
        isFavourite={isFavourite}
        onClick={toggleFavourite}
      />
      {imageSrc && (
        <img
          src={imageSrc}
          ref={zoomedImage}
          alt={imageTitle}
          height={448}
          width={400}
          style={{
            backgroundImage: `url(${imageSrc})`,
            height: '448px',
            width: '100%',
            display: 'none',
          }}
        />
      )}
    </div>
  );
};
